<template>
  <el-dialog
    title="定义签字区"
    :visible.sync="dialogVisible"
    width="820px"
    :close-on-click-modal="false"
    :before-close="handleDialogClose"
    custom-class="sign-file-dialog"
    v-loading="pageLoading"
    element-loading-text="加载中..."
    @close="closeHandler"
    lock-scroll
  >
    <div class="sign-file-content">
      <el-scrollbar class="sign-file-scroll">
        <!-- 顶部图片列表 -->
        <div class="swiper-preview-wrapper" @click.stop>
          <swiper-preview
            ref="swiperRef"
            :list="pages"
            :height="120"
            :item-width="120"
            :visible-count="5"
            :active-index="currentPageIndex"
            image-key="imageUrl"
            @item-click="onPageClick"
            @upload-success="imageUploadSuccessHandler"
            :fileType="fileType"
          >
            <template v-slot="{ item }">
              <img
                :src="item.imageUrl"
                style="width: 100%; height: 100%; object-fit: contain"
              />
            </template>
          </swiper-preview>
        </div>

        <!-- 签名画布区域 - 固定750px宽度 -->
        <div
          class="canvas-container"
          @mousedown="onPageMouseDown"
          @mousemove="onPageMouseMove"
          @mouseup="onPageMouseUp"
        >
          <sign-canvas
            v-if="currentPage"
            ref="signCanvasRef"
            :page="currentPage"
            :regions="currentRegions"
            :mode="editMode"
            :active-tool="activeTool"
            :selected-region-id="selectedRegionId"
            :canvas-width="750"
            @region-add="onRegionAdd"
            @region-update="onRegionUpdate"
            @region-delete="onRegionDelete"
            @region-select="onRegionSelect"
            @drag-start="onRegionDragStart"
            @drag-end="onRegionDragEnd"
          />
        </div>
      </el-scrollbar>

      <!-- 属性编辑栏（选中文本区或输入区时显示） - 固定在可视区域左下角 -->
      <div
        v-if="
          selectedRegion &&
          editMode === 'edit' &&
          selectedRegion.type !== 'signature'
        "
        class="property-bar"
        @click.stop
        @mousedown="onPropertyBarMouse"
        @mouseup="onPropertyBarMouse"
      >
        <div class="property-bar-content">
          <el-select
            v-model="currentFontSizeValue"
            size="small"
            style="width: 100px"
            @change="onFontSizeChange"
          >
            <el-option
              v-for="item in fontSizeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
          <div class="color-picker-row">
            <div
              v-for="color in presetColors"
              :key="color.value"
              class="color-item"
              :style="{
                borderColor:
                  currentFontColor === color.value
                    ? color.value
                    : 'transparent',
              }"
              :class="currentFontColor === color.value ? 'active' : ''"
              @click="onColorSelect(color.value)"
            >
              <div
                class="color-item-wrap"
                :style="'background-color:' + color.value"
              >
                <span v-if="currentFontColor === color.value" class="check-icon"
                  >✓</span
                >
              </div>
            </div>
            <div
              class="color-item custom-color"
              :class="isCustomColor ? 'active' : ''"
              :style="{
                borderColor: isCustomColor ? currentFontColor : 'transparent',
              }"
              @click="showCustomColorPicker"
            >
              <div
                class="color-item-wrap"
                :style="
                  isCustomColor ? { backgroundColor: currentFontColor } : {}
                "
              >
                <img
                  v-if="!isCustomColor"
                  :src="imageUrl2 + '/12jielong/bdJL/color-picker.png'"
                />
                <span v-else class="check-icon">✓</span>
              </div>
            </div>
          </div>
          <div class="delete-btn" @click="onDeleteRegion">
            <i
              class="el-icon-delete"
              style="font-size: 18px; color: #f56c6c"
            ></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div slot="footer" class="dialog-footer">
      <div class="toolbar-left">
        <template v-if="editMode === 'edit'">
          <div
            :class="'tool-item' + (activeTool === 'text' ? ' active' : '')"
            @click="setActiveTool('text')"
          >
            <i class="el-icon-document"></i>
            <span>文本区</span>
          </div>
          <div
            :class="'tool-item' + (activeTool === 'signature' ? ' active' : '')"
            @click="setActiveTool('signature')"
          >
            <i class="el-icon-edit"></i>
            <span>签字区</span>
          </div>
          <div
            :class="'tool-item' + (activeTool === 'input' ? ' active' : '')"
            @click="setActiveTool('input')"
          >
            <i class="el-icon-edit-outline"></i>
            <span>输入区</span>
          </div>
        </template>
        <div class="tool-item" @click="toggleMode">
          <i :class="editMode === 'edit' ? 'el-icon-view' : 'el-icon-edit'"></i>
          <span>{{ editMode === "edit" ? "预览" : "编辑" }}</span>
        </div>
      </div>
      <div class="toolbar-right">
        <el-button @click="handleCancel">取 消</el-button>
        <el-button type="warning" @click="handleSave">保 存</el-button>
      </div>
    </div>

    <!-- 文本编辑弹窗 -->
    <text-edit-popup
      v-if="showTextPopup"
      :visible.sync="showTextPopup"
      :region="editingRegion"
      @confirm="onTextEditConfirm"
      @cancel="onTextEditCancel"
    />

    <!-- 自定义颜色选择器 -->
    <color-picker
      :visible.sync="showColorPicker"
      :value="currentFontColor"
      @confirm="onCustomColorConfirm"
      @cancel="onCustomColorCancel"
    />
  </el-dialog>
</template>

<script>
import SwiperPreview from "./components/swiperPreview/swiperPreview.vue";
import SignCanvas from "./components/SignCanvas/SignCanvas.vue";
import TextEditPopup from "./components/TextEditPopup/TextEditPopup.vue";
import ColorPicker from "./components/ColorPicker/ColorPicker.vue";
import { imageUrl } from "@/utils/config/commonUrl.js";
import {
  REGION_TYPE,
  PRESET_COLORS,
  FONT_SIZE_OPTIONS,
  EDIT_MODE,
  TOOL_TYPE,
  createRegion,
} from "./constant";

export default {
  components: {
    SwiperPreview,
    SignCanvas,
    TextEditPopup,
    ColorPicker,
  },

  data() {
    return {
      dialogVisible: false,
      pageLoading: true,
      isDirty: false,
      imageUrl2: imageUrl,
      fileId: "",
      fileType: "file",
      templateId: "",
      pages: [],
      currentPageIndex: 0,
      editMode: EDIT_MODE.EDIT,
      activeTool: TOOL_TYPE.NONE,
      selectedRegionId: null,
      showTextPopup: false,
      editingRegion: null,
      isNewTextRegion: false,
      mouseStartX: 0,
      mouseStartY: 0,
      mouseStartTime: 0,
      isMouseMoved: false,
      isPropertyBarClicked: false,
      presetColors: PRESET_COLORS,
      fontSizeOptions: FONT_SIZE_OPTIONS,
      showColorPicker: false,
      isDraggingRegion: false,
    };
  },

  computed: {
    // 当前页面
    currentPage() {
      return this.pages[this.currentPageIndex] || null;
    },

    // 当前页面的区域列表
    currentRegions() {
      return this.currentPage?.regions || [];
    },

    // 选中的区域
    selectedRegion() {
      if (!this.selectedRegionId) return null;
      return this.currentRegions.find((r) => r.id === this.selectedRegionId);
    },

    // 当前字体颜色
    currentFontColor() {
      return this.selectedRegion?.config?.fontColor || "#333333";
    },

    // 当前字体大小值 (用于 el-select v-model)
    currentFontSizeValue: {
      get() {
        return this.selectedRegion?.config?.fontSize || 24;
      },
      set(val) {
        // setter 由 onFontSizeChange 处理
      },
    },

    // 是否是自定义颜色（不在预设颜色列表中）
    isCustomColor() {
      const color = this.currentFontColor.toUpperCase();
      return !this.presetColors.some((c) => c.value.toUpperCase() === color);
    },
  },

  // mounted() {
  //   // 从路由获取参数
  //   const query = this.$route.query || {};
  //   const mode = query.mode || "create";
  //   const fileType = query.fileType || "file";
  //   this.fileType = fileType;
  //   // 根据模式设置编辑状态
  //   if (mode === "preview") {
  //     this.editMode = "preview";
  //   } else {
  //     this.editMode = "edit";
  //   }
  //   // 初始化数据（从 store 获取）
  //   this.initFromStore(mode);
  // },

  methods: {
    initData(res) {
      console.log("initData", res);
      //   const query = this.$route.query || {};
      const mode = res.mode || "create";
      this.fileType = res.fileType || "file";
      this.originalFileName =
        this.fileType === "image" ? "签字图片" : res.data.originalFileName;
      this.siginFileIndex = res.siginFileIndex;
      // 根据模式设置编辑状态
      if (mode === "preview") {
        this.editMode = "preview";
      } else {
        this.editMode = "edit";
      }
      this.dialogVisible = true;
      // 初始化数据（从 store 获取）
      this.initFromStore(mode, res.data);
    },

    closeHandler() {
      this.resetHandler();
      this.$emit("closeHandler");
    },

    resetHandler() {
      this.dialogVisible = false;
      this.pageLoading = true;
      this.isDirty = false;
      this.imageUrl2 = imageUrl;
      this.fileId = "";
      this.fileType = "file";
      this.templateId = "";
      this.pages = [];
      this.currentPageIndex = 0;
      this.editMode = EDIT_MODE.EDIT;
      this.activeTool = TOOL_TYPE.NONE;
      this.selectedRegionId = null;
      this.showTextPopup = false;
      this.editingRegion = null;
      this.isNewTextRegion = false;
      this.mouseStartX = 0;
      this.mouseStartY = 0;
      this.mouseStartTime = 0;
      this.isMouseMoved = false;
      this.isPropertyBarClicked = false;
      this.presetColors = PRESET_COLORS;
      this.fontSizeOptions = FONT_SIZE_OPTIONS;
      this.showColorPicker = false;
      this.isDraggingRegion = false;
    },

    // 标记数据已修改
    markDirty() {
      if (!this.isDirty) {
        this.isDirty = true;
      }
    },
    imageUploadSuccessHandler(uploadImageList) {
      this.pages = [...this.pages, ...uploadImageList].map((item, index) => {
        return {
          ...item,
          pageIndex: index,
        };
      });
      this.markDirty();
      //TODO 需要缓存替换
    },
    // 从 store 初始化数据
    initFromStore(mode, data) {
      const siginFileInfo = data || {};
      this.fileId = siginFileInfo.fileId || "";
      if (siginFileInfo.pages && siginFileInfo.pages.length > 0) {
        // 深拷贝避免直接修改 store
        const pages = JSON.parse(JSON.stringify(siginFileInfo.pages));

        if (mode === "create") {
          // 创建模式：清空所有 regions
          this.pages = pages.map((page) => ({
            ...page,
            regions: [],
          }));
        } else {
          // 编辑/预览模式：保留 regions
          this.pages = pages;
        }
      } else {
        // 没有数据时使用默认空页面
        this.pages = [];
      }
      // 数据加载完成，关闭loading
      this.$nextTick(() => {
        this.pageLoading = false;
      });
    },

    // 切换页面
    onPageClick({ item, index }) {
      this.currentPageIndex = index;
      this.selectedRegionId = null;
      this.activeTool = TOOL_TYPE.NONE;
    },

    // 切换编辑/预览模式
    toggleMode() {
      this.editMode =
        this.editMode === EDIT_MODE.EDIT ? EDIT_MODE.PREVIEW : EDIT_MODE.EDIT;
      if (this.editMode === EDIT_MODE.PREVIEW) {
        this.selectedRegionId = null;
        this.activeTool = TOOL_TYPE.NONE;
      }
    },

    // 设置当前工具 - 直接在固定位置生成区域
    setActiveTool(tool) {
      if (!this.currentPage) return;

      // 获取画布容器尺寸
      var containerSize = null;
      if (this.$refs.signCanvasRef) {
        containerSize = this.$refs.signCanvasRef.getContainerSize();
      }

      // 直接创建区域（传入容器尺寸用于计算固定像素对应的百分比）
      // 位置：水平居中，垂直顶部对齐（y=0）
      // 使用 x=-1 作为特殊标记，让 createRegion 自动计算水平居中
      var region = createRegion(
        tool,
        { x: -1, y: 0 }, // x=-1 表示水平居中，y=0 表示顶部对齐
        this.currentPage.pageId,
        containerSize
      );

      // 如果是文本区，先弹出编辑框，保存后才添加到画布
      if (tool === REGION_TYPE.TEXT) {
        this.editingRegion = { ...region };
        this.isNewTextRegion = true;
        this.showTextPopup = true;
        return;
      }

      // 其他类型直接添加到当前页面
      this.pages[this.currentPageIndex].regions.push(region);
      this.markDirty();
      // 使用 $nextTick 确保响应式更新后再设置选中状态
      this.$nextTick(() => {
        this.selectedRegionId = region.id;
      });
    },

    // 添加区域
    onRegionAdd(region) {
      const pageIndex = this.currentPageIndex;
      this.pages[pageIndex].regions.push(region);
      this.selectedRegionId = region.id;
      this.activeTool = TOOL_TYPE.NONE;

      // 如果是文本区，弹出编辑框
      if (region.type === REGION_TYPE.TEXT) {
        this.editingRegion = { ...region };
        this.showTextPopup = true;
      }
    },

    // 更新区域
    onRegionUpdate(region) {
      const pageIndex = this.currentPageIndex;
      const regionIndex = this.pages[pageIndex].regions.findIndex(
        (r) => r.id === region.id
      );
      if (regionIndex >= 0) {
        // 直接修改，避免创建新对象（提升性能）
        var existingRegion = this.pages[pageIndex].regions[regionIndex];
        existingRegion.position.x = region.position.x;
        existingRegion.position.y = region.position.y;
        existingRegion.position.width = region.position.width;
        existingRegion.position.height = region.position.height;
        if (region.config) {
          Object.assign(existingRegion.config, region.config);
        }
        existingRegion.updatedAt = Date.now();
        this.markDirty();
      }
    },

    // 删除区域
    onRegionDelete(regionId) {
      const pageIndex = this.currentPageIndex;
      const regionIndex = this.pages[pageIndex].regions.findIndex(
        (r) => r.id === regionId
      );
      if (regionIndex >= 0) {
        this.pages[pageIndex].regions.splice(regionIndex, 1);
        if (this.selectedRegionId === regionId) {
          this.selectedRegionId = null;
        }
        this.markDirty();
      }
    },

    // 选中区域
    onRegionSelect(data) {
      // data 可能是 null（点击空白处取消选中）或 { regionId, isClick }
      if (!data) {
        this.selectedRegionId = null;
        return;
      }

      const regionId = data.regionId;
      const isClick = data.isClick;

      this.selectedRegionId = regionId;
      const region = this.currentRegions.find((r) => r.id === regionId);

      // 点击文本区时打开弹层
      if (isClick && region && region.type === REGION_TYPE.TEXT) {
        this.editingRegion = { ...region };
        this.showTextPopup = true;
      }
    },

    // 删除选中区域
    onDeleteRegion() {
      if (this.selectedRegionId) {
        this.$confirm("确定删除该区域吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.onRegionDelete(this.selectedRegionId);
          })
          .catch(() => {});
      }
    },

    // 字体大小改变 (PC端 el-select)
    onFontSizeChange(fontSize) {
      if (this.selectedRegion) {
        var updatedRegion = {
          ...this.selectedRegion,
          config: {
            ...this.selectedRegion.config,
            fontSize,
          },
        };
        // 输入区：字体大小改变后，调整宽度以确保文字一行显示
        if (this.selectedRegion.type === "input") {
          updatedRegion = this.adjustInputRegionSize(updatedRegion);
        }
        this.onRegionUpdate(updatedRegion);
      }
    },

    // 调整输入区尺寸以确保文字一行显示
    adjustInputRegionSize(region) {
      if (!region || region.type !== "input") return region;

      var containerSize = null;
      if (this.$refs.signCanvasRef) {
        containerSize = this.$refs.signCanvasRef.getContainerSize();
      }
      if (!containerSize || !containerSize.width || !containerSize.height) {
        return region;
      }

      var placeholder = region.config.placeholder || "";
      if (placeholder.length === 0) return region;

      var fontSize = region.config.fontSize || 24;
      // PC端直接使用 fontSize 作为 px 值
      var fontSizePx = fontSize;
      var paddingPx = 8;
      var lineHeightPx = fontSizePx * 1.2;

      // 计算文字一行显示所需的宽度（增加10%安全边距）
      var textWidthPx = placeholder.length * fontSizePx * 1.1 + paddingPx;
      var requiredWidth = textWidthPx / containerSize.width;

      // 限制最大宽度为容器的 90%
      var maxWidth = 0.9;
      var newWidth = Math.min(requiredWidth, maxWidth);

      // 计算一行文字的高度
      var oneLineHeightPx = lineHeightPx + paddingPx;
      var newHeight = oneLineHeightPx / containerSize.height;

      // 计算原来的中心点
      var oldCenterX = region.position.x + region.position.width / 2;
      var oldCenterY = region.position.y + region.position.height / 2;

      // 根据新尺寸重新计算左上角坐标，保持中心点不变
      var newX = oldCenterX - newWidth / 2;
      var newY = oldCenterY - newHeight / 2;

      // 确保不超出边界
      newX = Math.max(0, Math.min(newX, 1 - newWidth));
      newY = Math.max(0, Math.min(newY, 1 - newHeight));

      return {
        ...region,
        position: {
          ...region.position,
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        },
      };
    },

    // 调整输入区高度以确保文字能完整显示（保留用于其他场景）
    adjustInputRegionHeight(region) {
      if (!region || region.type !== "input") return region;

      var containerSize = null;
      if (this.$refs.signCanvasRef) {
        containerSize = this.$refs.signCanvasRef.getContainerSize();
      }
      if (!containerSize || !containerSize.width || !containerSize.height) {
        return region;
      }

      var placeholder = region.config.placeholder || "";
      if (placeholder.length === 0) return region;

      var fontSize = region.config.fontSize || 24;
      // PC端直接使用 px
      var fontSizePx = fontSize / 2;
      var paddingPx = 4;
      var lineHeightPx = fontSizePx * 1.2;

      // 估算文字总宽度
      var textWidthPx = placeholder.length * fontSizePx;
      // 当前区域的实际像素宽度
      var regionWidthPx =
        region.position.width * containerSize.width - paddingPx;
      if (regionWidthPx < fontSizePx) regionWidthPx = fontSizePx;

      var minHeightPx;
      if (regionWidthPx >= textWidthPx) {
        // 一行能显示完
        minHeightPx = lineHeightPx + paddingPx;
      } else {
        // 需要多行
        var lines = Math.ceil(textWidthPx / regionWidthPx);
        minHeightPx = lines * lineHeightPx + paddingPx;
      }

      var minHeight = minHeightPx / containerSize.height;
      var newHeight = Math.max(region.position.height, minHeight);

      // 确保不超出底部边界
      if (region.position.y + newHeight > 1) {
        newHeight = 1 - region.position.y;
      }

      return {
        ...region,
        position: {
          ...region.position,
          height: newHeight,
        },
      };
    },

    // 颜色选择
    onColorSelect(color) {
      if (this.selectedRegion) {
        this.onRegionUpdate({
          ...this.selectedRegion,
          config: {
            ...this.selectedRegion.config,
            fontColor: color,
          },
        });
      }
    },

    // 显示自定义颜色选择器
    showCustomColorPicker() {
      this.showColorPicker = true;
    },

    // 自定义颜色确认
    onCustomColorConfirm(color) {
      this.showColorPicker = false;
      if (this.selectedRegion) {
        this.onRegionUpdate({
          ...this.selectedRegion,
          config: {
            ...this.selectedRegion.config,
            fontColor: color,
          },
        });
      }
    },

    // 自定义颜色取消
    onCustomColorCancel() {
      this.showColorPicker = false;
    },

    // 文本编辑取消
    onTextEditCancel() {
      this.showTextPopup = false;
      this.editingRegion = null;
      this.isNewTextRegion = false;
    },

    // 文本编辑确认
    onTextEditConfirm(region) {
      // 根据文本内容计算合适的宽度
      var adjustedRegion = this.calculateTextRegionSize(region);

      if (this.isNewTextRegion) {
        // 新建文本区域：保存后才添加到画布
        this.pages[this.currentPageIndex].regions.push(adjustedRegion);
        this.selectedRegionId = adjustedRegion.id;
        this.markDirty();
      } else {
        // 编辑已有区域
        this.onRegionUpdate(adjustedRegion);
      }
      this.showTextPopup = false;
      this.editingRegion = null;
      this.isNewTextRegion = false;
    },

    // 计算文本区域的合适尺寸
    calculateTextRegionSize(region) {
      if (region.type !== "text" || !region.config.content) {
        return region;
      }

      var containerSize = null;
      if (this.$refs.signCanvasRef) {
        containerSize = this.$refs.signCanvasRef.getContainerSize();
      }
      if (!containerSize || !containerSize.width) {
        return region;
      }

      var content = region.config.content;
      var fontSize = region.config.fontSize || 24;
      // PC端直接使用 px
      var fontSizePx = fontSize / 2;

      // 计算文本所需的宽度（单行显示）
      var textWidthPx = content.length * fontSizePx;
      // 限制最大宽度为容器的 80%
      var maxWidthPx = containerSize.width * 0.8;
      // 最小宽度
      var minWidthPx = 80;

      var finalWidthPx = Math.max(
        minWidthPx,
        Math.min(textWidthPx, maxWidthPx)
      );
      var finalWidth = finalWidthPx / containerSize.width;

      // 计算高度（单行高度）
      var lineHeightPx = fontSizePx * 1.2;
      var finalHeight = lineHeightPx / containerSize.height;

      // 计算原来的中心点
      var oldCenterX = region.position.x + region.position.width / 2;
      var oldCenterY = region.position.y + region.position.height / 2;

      // 根据新尺寸重新计算左上角坐标，保持中心点不变
      var newX = oldCenterX - finalWidth / 2;
      var newY = oldCenterY - finalHeight / 2;

      // 确保不超出边界
      newX = Math.max(0, Math.min(newX, 1 - finalWidth));
      newY = Math.max(0, Math.min(newY, 1 - finalHeight));

      return {
        ...region,
        position: {
          ...region.position,
          x: newX,
          y: newY,
          width: finalWidth,
          height: finalHeight,
        },
      };
    },

    // 检测两个区域是否重叠
    checkRegionsOverlap(region1, region2) {
      var pos1 = region1.position;
      var pos2 = region2.position;

      // 计算边界
      var left1 = pos1.x;
      var right1 = pos1.x + pos1.width;
      var top1 = pos1.y;
      var bottom1 = pos1.y + pos1.height;

      var left2 = pos2.x;
      var right2 = pos2.x + pos2.width;
      var top2 = pos2.y;
      var bottom2 = pos2.y + pos2.height;

      // 检测是否重叠
      return !(
        right1 <= left2 ||
        left1 >= right2 ||
        bottom1 <= top2 ||
        top1 >= bottom2
      );
    },
    // 验证保存数据，返回 { message, pageIndex } 或 null
    validateSaveData() {
      var self = this;

      // 检查所有页面中是否至少有一个签名区
      var hasAnySignature = this.pages.some(function (page) {
        return page.regions.some(function (r) {
          return r.type === REGION_TYPE.SIGNATURE;
        });
      });
      if (!hasAnySignature) {
        return { message: "请至少在一个页面添加签名区", pageIndex: 0 };
      }

      // 检查每个页面的区域是否重叠
      for (var pageIndex = 0; pageIndex < this.pages.length; pageIndex++) {
        var regions = this.pages[pageIndex].regions;
        for (var i = 0; i < regions.length; i++) {
          for (var j = i + 1; j < regions.length; j++) {
            if (self.checkRegionsOverlap(regions[i], regions[j])) {
              return {
                message:
                  "第" + (pageIndex + 1) + "页存在区域重叠，请调整区域位置",
                pageIndex: pageIndex,
              };
            }
          }
        }
      }

      return null;
    },

    // 取消选中区域
    clearSelection() {
      this.selectedRegionId = null;
    },

    // 页面鼠标按下 (PC端)
    onPageMouseDown(e) {
      this.mouseStartX = e.clientX;
      this.mouseStartY = e.clientY;
      this.mouseStartTime = Date.now();
      this.isMouseMoved = false;
    },

    // 页面鼠标移动 (PC端)
    onPageMouseMove(e) {
      // 检测是否有明显移动（超过 10px 认为是拖拽）
      var deltaX = Math.abs(e.clientX - this.mouseStartX);
      var deltaY = Math.abs(e.clientY - this.mouseStartY);
      if (deltaX > 10 || deltaY > 10) {
        this.isMouseMoved = true;
      }
    },

    // 页面鼠标抬起 (PC端)
    onPageMouseUp(e) {
      // 如果点击了属性栏，不处理
      if (this.isPropertyBarClicked) {
        this.isPropertyBarClicked = false;
        return;
      }
      // 如果文本编辑弹层或颜色选择器打开，不处理（保持选中状态）
      if (this.showTextPopup || this.showColorPicker) {
        return;
      }
      var clickDuration = Date.now() - this.mouseStartTime;
      // 只有在没有移动且点击时间短（<300ms）的情况下才认为是点击
      if (!this.isMouseMoved && clickDuration < 300) {
        // 如果有选中区域，则取消选中
        if (this.selectedRegionId) {
          this.selectedRegionId = null;
        }
      }
    },

    // 属性栏鼠标事件 (PC端)
    onPropertyBarMouse() {
      this.isPropertyBarClicked = true;
    },

    // 区域开始拖拽/缩放（禁止scroll-view滚动）
    onRegionDragStart() {
      this.isDraggingRegion = true;
    },

    // 区域结束拖拽/缩放（恢复scroll-view滚动）
    onRegionDragEnd() {
      this.isDraggingRegion = false;
    },

    // 取消
    handleCancel() {
      if (this.isDirty) {
        this.$confirm("有未保存的修改，确定要离开吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.dialogVisible = false;
          })
          .catch(() => {});
      } else {
        this.dialogVisible = false;
      }
    },

    // 弹层关闭前
    handleDialogClose(done) {
      if (this.isDirty) {
        this.$confirm("有未保存的修改，确定要离开吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            done();
          })
          .catch(() => {});
      } else {
        done();
      }
    },

    // 保存
    async handleSave() {
      // 验证数据
      var error = this.validateSaveData();
      if (error) {
        var self = this;
        this.$alert(error.message, "保存失败", {
          confirmButtonText: "确定",
          callback: function () {
            // 点击确定后跳转到有问题的页面
            self.currentPageIndex = error.pageIndex;
          },
        });
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: "保存中...",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.7)",
      });

      try {
        var data = {
          fileId: this.fileId,
          originalFileName: this.originalFileName,
          fileType: this.fileType,
          siginFileIndex: this.siginFileIndex,
          pages: this.pages.map(function (page) {
            return {
              pageId: page.pageId,
              pageIndex: page.pageIndex,
              imageUrl: page.imageUrl,
              regions: page.regions.map(function (region) {
                return {
                  id: region.id,
                  type: region.type,
                  position: region.position,
                  config: region.config,
                };
              }),
            };
          }),
        };

        console.log("========== 保存数据 ==========");
        console.log(JSON.stringify(data, null, 2));
        console.log("==============================");
        this.$emit("saveHandler", JSON.stringify(data));
        this.resetHandler();
        loading.close();
        this.isDirty = false;
      } catch (err) {
        loading.close();
        console.error("保存失败:", err);
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./siginFile.scss"></style>

<style lang="scss">
.sign-file-dialog {
  user-select: none;
}
</style>
